/**
 * CCPA-compliant cookie consent state manager.
 *
 * Stores the visitor's analytics preference in localStorage and honors the
 * Global Privacy Control (GPC) browser signal, which — when present and true —
 * overrides any stored opt-in.
 */

declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

export const CONSENT_VERSION = 1;
const STORAGE_KEY = "cookie_consent";

export interface ConsentPreferences {
  analytics: boolean;
}

interface StoredConsent extends ConsentPreferences {
  timestamp: string;
  version: number;
}

const DEFAULT_CONSENT: ConsentPreferences = { analytics: true };

/**
 * Read a structurally valid stored consent record (analytics is a boolean),
 * regardless of its version. Corrupt or partial records return null. Version is
 * intentionally NOT checked here so a prior choice can be preserved across a
 * version bump while the banner re-prompts (see hasInteracted).
 */
const readStored = (): StoredConsent | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as StoredConsent).analytics !== "boolean"
    ) {
      return null;
    }
    return parsed as StoredConsent;
  } catch {
    return null;
  }
};

/**
 * Read the stored consent preferences. Returns the default consent when no
 * valid preference has been stored, otherwise preserves the prior explicit
 * choice — even across a consent-version bump, so a user who opted out is not
 * silently re-enabled.
 */
export const getConsent = (): ConsentPreferences => {
  const stored = readStored();
  if (!stored) return { ...DEFAULT_CONSENT };
  return { analytics: stored.analytics };
};

/**
 * Persist the visitor's consent preferences, stamping the current time and
 * schema version.
 */
export const setConsent = (prefs: ConsentPreferences): void => {
  const record: StoredConsent = {
    ...prefs,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
};

/**
 * Whether the visitor has made an explicit consent choice under the CURRENT
 * consent version. An outdated-version record returns false so the banner
 * re-prompts, while getConsent() still honors the stored choice until then.
 */
export const hasInteracted = (): boolean => {
  const stored = readStored();
  return stored !== null && stored.version === CONSENT_VERSION;
};

/**
 * Whether the Global Privacy Control browser signal is present and enabled.
 */
export const isGpcEnabled = (): boolean => navigator.globalPrivacyControl === true;

/**
 * Whether analytics may run. The Global Privacy Control signal, when present
 * and true, overrides any stored opt-in.
 */
export const isAnalyticsAllowed = (): boolean => {
  if (isGpcEnabled()) return false;
  return getConsent().analytics;
};

/**
 * Remove the stored consent preference.
 */
export const resetConsent = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};
